import { useBookings, useRoomTypes, useRooms } from '@hooks/accommodation';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  selectAccommodationProperty,
  setSelectedBooking,
} from '@store/accommodation';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { AccommodationBooking } from '../../../../types/accommodation';
import {
  BookingStatusBadge,
  STATUS_TIMELINE_CLASSES,
} from './BookingStatusBadge';

// ─── Shared date helpers ──────────────────────────────────────────────────────

function isoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function addDays(dateStr: string, n: number): string {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

/** How many days is b after a (can be negative). */
function diffDays(a: string, b: string): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  const da = new Date(`${a}T00:00:00`).getTime();
  const db = new Date(`${b}T00:00:00`).getTime();
  return Math.round((db - da) / msPerDay);
}

// ─── Monthly calendar helpers ─────────────────────────────────────────────────

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function firstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay(); // 0 = Sunday
}

function bookingsForDay(
  bookings: AccommodationBooking[],
  dateStr: string,
): AccommodationBooking[] {
  return bookings.filter((b) => b.check_in <= dateStr && b.check_out > dateStr);
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAY_ABBREVS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ─── Gantt layout constants ───────────────────────────────────────────────────

const DAY_WIDTH = 72; // px per day column
const ROW_HEIGHT = 48; // px per room row
const LABEL_WIDTH = 160; // px for room label column
const VISIBLE_DAYS = 14;

// ─── BookingCalendar (monthly grid) ──────────────────────────────────────────

export const BookingCalendar: React.FC = () => {
  const dispatch = useAppDispatch();
  const property = useAppSelector(selectAccommodationProperty);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  const calFrom = isoDate(year, month, 1);
  const calTo = isoDate(year, month, daysInMonth(year, month));
  const { data: bookings = [] } = useBookings(property?.id, {
    stay_from: calFrom,
    stay_to: calTo,
  });

  const goPrev = () => {
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
  };

  const goNext = () => {
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else setMonth((m) => m + 1);
  };

  const totalDays = daysInMonth(year, month);
  const startOffset = firstDayOfMonth(year, month);

  const cells = useMemo(() => {
    const arr: Array<{ day: number | null; key: string }> = [];
    for (let i = 0; i < startOffset; i += 1)
      arr.push({ day: null, key: `pad-${i}` });
    for (let d = 1; d <= totalDays; d += 1)
      arr.push({ day: d, key: `day-${d}` });
    return arr;
  }, [totalDays, startOffset]);

  const todayStr = now.toISOString().slice(0, 10);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={goPrev}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
        >
          <ChevronLeft size={16} />
        </button>
        <h4 className="text-sm font-semibold text-gray-900">
          {MONTH_NAMES[month]} {year}
        </h4>
        <button
          type="button"
          onClick={goNext}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Day-of-week headers */}
      <div className="mb-1 grid grid-cols-7 text-center">
        {WEEKDAY_ABBREVS.map((d) => (
          <div key={d} className="py-1 text-xs font-semibold text-gray-400">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {cells.map((cell) => {
          if (cell.day === null) {
            return (
              <div
                key={cell.key}
                className="min-h-[80px] border-t border-gray-100"
              />
            );
          }
          const dateStr = isoDate(year, month, cell.day);
          const dayBookings = bookingsForDay(bookings, dateStr);
          const isToday = dateStr === todayStr;

          return (
            <div
              key={dateStr}
              className="min-h-[80px] border-t border-gray-100 p-1"
            >
              <span
                className={[
                  'flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
                  isToday ? 'bg-pos-primary text-white' : 'text-gray-700',
                ].join(' ')}
              >
                {cell.day}
              </span>
              <div className="mt-0.5 space-y-0.5">
                {dayBookings.slice(0, 3).map((b) => {
                  const name = b.client
                    ? `${b.client.first_name} ${b.client.last_name[0]}.`
                    : 'Guest';
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => dispatch(setSelectedBooking(b))}
                      className="block w-full truncate rounded bg-pos-primary/10 px-1 py-0.5 text-left text-[10px] font-medium text-pos-primary hover:bg-pos-primary/20"
                    >
                      {name}
                    </button>
                  );
                })}
                {dayBookings.length > 3 && (
                  <p className="text-[10px] text-gray-400">
                    +{dayBookings.length - 3} more
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-3">
        {(['confirmed', 'checked_in', 'checked_out', 'cancelled'] as const).map(
          (s) => (
            <BookingStatusBadge key={s} status={s} />
          ),
        )}
      </div>
    </div>
  );
};

// ─── DailyBookingCalendar (Gantt-style timeline) ──────────────────────────────

function formatDateRange(start: string, end: string): string {
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const sDay = s.getDate();
  const sMon = months[s.getMonth()];
  const eDay = e.getDate();
  const eMon = months[e.getMonth()];
  const eYear = e.getFullYear();

  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${sDay}–${eDay} ${eMon} ${eYear}`;
  }
  if (s.getFullYear() === e.getFullYear()) {
    return `${sDay} ${sMon} – ${eDay} ${eMon} ${eYear}`;
  }
  return `${sDay} ${sMon} ${s.getFullYear()} – ${eDay} ${eMon} ${eYear}`;
}

export const DailyBookingCalendar: React.FC = () => {
  const dispatch = useAppDispatch();
  const property = useAppSelector(selectAccommodationProperty);

  const { data: allRoomTypes = [] } = useRoomTypes(property?.id);
  const { data: allRooms = [], isLoading } = useRooms(property?.id);
  const rooms = allRooms.filter((r) => r.is_active);
  const roomTypes = allRoomTypes.filter((rt) => rt.is_active);

  // Anchor: the leftmost visible date (windowStart)
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [windowStart, setWindowStart] = useState<string>(addDays(todayStr, -0));

  const fetchFrom = addDays(windowStart, -14);
  const fetchTo = addDays(windowStart, VISIBLE_DAYS + 14);
  const { data: bookings = [] } = useBookings(property?.id, {
    stay_from: fetchFrom,
    stay_to: fetchTo,
  });

  const windowEnd = addDays(windowStart, VISIBLE_DAYS - 1);

  // Build array of 14 date strings for the visible window
  const dates = useMemo(() => {
    const arr: string[] = [];
    for (let i = 0; i < VISIBLE_DAYS; i += 1) {
      arr.push(addDays(windowStart, i));
    }
    return arr;
  }, [windowStart]);

  const goPrev = () => setWindowStart((s) => addDays(s, -7));
  const goNext = () => setWindowStart((s) => addDays(s, 7));
  const goToday = () => setWindowStart(todayStr);

  const bookingsForRoom = (roomId: string): AccommodationBooking[] =>
    bookings.filter((b) =>
      b.booking_rooms?.some((br) => br.room_id === roomId),
    );

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      {/* Controls */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goPrev}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
            aria-label="Previous 7 days"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={goToday}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 border border-gray-200"
          >
            Today
          </button>
          <button
            type="button"
            onClick={goNext}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
            aria-label="Next 7 days"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <span className="text-sm font-semibold text-gray-800">
          {formatDateRange(windowStart, windowEnd)}
        </span>

        {isLoading && (
          <span className="text-xs text-gray-400 animate-pulse">Loading…</span>
        )}
        {!isLoading && <span className="w-16" />}
      </div>

      {/* Timeline grid — horizontal scroll for date columns, vertical for rooms.
          Room labels stay sticky at left: 0; date header stays sticky at top: 0 */}
      <div className="overflow-auto max-h-[calc(100vh-260px)]">
        <div style={{ minWidth: LABEL_WIDTH + VISIBLE_DAYS * DAY_WIDTH }}>
          {/* Date header row */}
          <div className="flex border-b border-gray-100 sticky top-0 bg-white z-20">
            {/* Sticky room-column header */}
            <div
              style={{
                width: LABEL_WIDTH,
                minWidth: LABEL_WIDTH,
                position: 'sticky',
                left: 0,
                zIndex: 21,
                backgroundColor: 'white',
              }}
              className="shrink-0 px-3 py-2 text-xs font-semibold text-gray-400 border-r border-gray-100"
            >
              Room
            </div>
            {dates.map((d) => {
              const dateObj = new Date(`${d}T00:00:00`);
              const isToday = d === todayStr;
              const weekday = WEEKDAY_ABBREVS[dateObj.getDay()];
              const dayNum = dateObj.getDate();
              return (
                <div
                  key={d}
                  style={{ width: DAY_WIDTH, minWidth: DAY_WIDTH }}
                  className={[
                    'shrink-0 flex flex-col items-center justify-center py-2 text-xs border-r border-gray-100',
                    isToday
                      ? 'bg-pos-primary/5 font-semibold text-pos-primary'
                      : 'text-gray-500',
                  ].join(' ')}
                >
                  <span className="text-[10px] leading-none">{weekday}</span>
                  <span
                    className={[
                      'mt-1 flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium',
                      isToday ? 'bg-pos-primary text-white' : '',
                    ].join(' ')}
                  >
                    {dayNum}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Room type groups */}
          {roomTypes.map((rt) => {
            const typeRooms = rooms.filter((r) => r.room_type_id === rt.id);
            if (!typeRooms.length) return null;

            return (
              <div key={rt.id}>
                {/* Room type header */}
                <div className="flex bg-gray-50 border-b border-gray-100">
                  {/* Sticky room-type label */}
                  <div
                    style={{
                      width: LABEL_WIDTH,
                      minWidth: LABEL_WIDTH,
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                      backgroundColor: 'rgb(249 250 251)', // gray-50
                    }}
                    className="shrink-0 px-3 py-1.5 text-xs font-semibold text-gray-600 border-r border-gray-100"
                  >
                    {rt.name}
                  </div>
                  <div style={{ width: VISIBLE_DAYS * DAY_WIDTH }} />
                </div>

                {/* Individual room rows */}
                {typeRooms.map((room) => {
                  const roomBookings = bookingsForRoom(room.id);

                  return (
                    <div
                      key={room.id}
                      className="flex border-b border-gray-50 hover:bg-gray-50/50"
                    >
                      {/* Sticky room label */}
                      <div
                        style={{
                          width: LABEL_WIDTH,
                          minWidth: LABEL_WIDTH,
                          height: ROW_HEIGHT,
                          position: 'sticky',
                          left: 0,
                          zIndex: 10,
                          backgroundColor: 'white',
                        }}
                        className="shrink-0 px-3 text-xs text-gray-600 flex items-center border-r border-gray-100"
                      >
                        <span>Room {room.room_number}</span>
                        {room.floor && (
                          <span className="ml-1 text-gray-400">
                            · F{room.floor}
                          </span>
                        )}
                      </div>

                      {/* Booking spans area */}
                      <div
                        style={{
                          width: VISIBLE_DAYS * DAY_WIDTH,
                          height: ROW_HEIGHT,
                          position: 'relative',
                        }}
                      >
                        {/* Day grid lines */}
                        {dates.map((d, i) => (
                          <div
                            key={d}
                            style={{
                              position: 'absolute',
                              left: i * DAY_WIDTH,
                              top: 0,
                              width: 1,
                              height: '100%',
                            }}
                            className="bg-gray-100"
                          />
                        ))}

                        {/* Today highlight column */}
                        {dates.map((d, i) => {
                          if (d !== todayStr) return null;
                          return (
                            <div
                              key={`today-col-${d}`}
                              style={{
                                position: 'absolute',
                                left: i * DAY_WIDTH,
                                top: 0,
                                width: DAY_WIDTH,
                                height: '100%',
                              }}
                              className="bg-pos-primary/5 pointer-events-none"
                            />
                          );
                        })}

                        {/* Booking spans */}
                        {roomBookings.map((booking) => {
                          const startOff = Math.max(
                            0,
                            diffDays(windowStart, booking.check_in),
                          );
                          const endOff = Math.min(
                            VISIBLE_DAYS,
                            diffDays(windowStart, booking.check_out) + 1,
                          );

                          if (endOff <= 0 || startOff >= VISIBLE_DAYS)
                            return null;

                          const guestName = booking.client
                            ? `${booking.client.first_name} ${booking.client.last_name[0]}.`
                            : 'Guest';

                          const spanWidth = (endOff - startOff) * DAY_WIDTH - 4;
                          if (spanWidth <= 0) return null;

                          return (
                            <button
                              key={booking.id}
                              type="button"
                              onClick={() =>
                                dispatch(setSelectedBooking(booking))
                              }
                              style={{
                                position: 'absolute',
                                left: startOff * DAY_WIDTH + 2,
                                width: spanWidth,
                                top: 6,
                                height: ROW_HEIGHT - 12,
                                borderRadius: 6,
                              }}
                              className={`${STATUS_TIMELINE_CLASSES[booking.status]} truncate text-left px-2 text-xs font-medium flex items-center hover:opacity-90 transition-opacity`}
                              title={`${guestName} · ${booking.check_in} → ${booking.check_out}`}
                            >
                              {guestName}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Empty / loading state */}
          {rooms.length === 0 && (
            <div className="flex h-32 items-center justify-center text-sm text-gray-400">
              {isLoading ? 'Loading rooms…' : 'No rooms configured'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
